// Real DHL API Integration Service
class RealDHLService {
  constructor() {
    this.baseURL = 'https://test.api.dhl.com';
    this.apiKey = process.env.REACT_APP_DHL_API_KEY;
    this.accountNumber = process.env.REACT_APP_DHL_ACCOUNT;
  }

  // Get shipping rates from DHL
  async getRates(origin, destination, weight, dimensions = {}) {
    try {
      const requestBody = {
        customerType: 'business',
        accounts: [{
          typeCode: 'shipper',
          number: this.accountNumber
        }],
        plannedShippingDateAndTime: new Date().toISOString(),
        unitOfMeasurement: 'SI',
        packages: [{
          weight: weight,
          dimensions: {
            length: dimensions.length || 10,
            width: dimensions.width || 10,
            height: dimensions.height || 10
          }
        }],
        content: 'DOCUMENTS',
        serviceType: 'express',
        ship: {
          shipper: {
            postalAddress: {
              streetLines: [origin.street],
              city: origin.city,
              stateOrProvinceCode: origin.state,
              postalCode: origin.zipCode,
              countryCode: origin.country || 'MX'
            }
          },
          recipient: {
            postalAddress: {
              streetLines: [destination.street],
              city: destination.city,
              stateOrProvinceCode: destination.state,
              postalCode: destination.zipCode,
              countryCode: destination.country || 'MX'
            }
          }
        }
      };

      const response = await fetch(`${this.baseURL}/parcel/de/v2/rates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`DHL API error: ${response.status}`);
      }

      const data = await response.json();
      return this.parseDHLRates(data);
    } catch (error) {
      console.error('Error getting DHL rates:', error);
      // Fallback to demo rates if API fails
      return this.getDemoRates(origin, destination, weight);
    }
  }

  // Parse DHL rates response
  parseDHLRates(data) {
    if (!data.products) {
      return [];
    }

    return data.products.map(product => ({
      carrier: 'DHL',
      service: product.productName,
      serviceCode: product.productCode,
      cost: parseFloat(product.totalPrice),
      currency: product.totalPriceCurrency || 'USD',
      deliveryTime: this.getDeliveryTime(product.deliveryTime),
      guaranteed: product.deliveryTime?.guaranteed || false,
      transitDays: product.deliveryTime?.days || 1
    }));
  }

  // Get delivery time based on service type
  getDeliveryTime(deliveryTime) {
    if (!deliveryTime) return '2-3 días';
    
    const days = deliveryTime.days || 1;
    if (days === 1) return '1 día';
    if (days === 2) return '1-2 días';
    if (days <= 3) return '2-3 días';
    return `${days} días`;
  }

  // Create shipment with DHL
  async createShipment(shipmentData) {
    try {
      const requestBody = {
        plannedShippingDateAndTime: new Date().toISOString(),
        pickup: {
          isRequested: false,
          closeTime: '18:00',
          location: 'Reception'
        },
        productCode: shipmentData.serviceCode || 'N',
        localProductCode: shipmentData.serviceCode || 'N',
        getRateEstimates: false,
        accounts: [{
          typeCode: 'shipper',
          number: this.accountNumber
        }],
        customerDetails: {
          shipperDetails: {
            postalAddress: {
              streetLines: [shipmentData.origin.address],
              city: shipmentData.origin.city,
              stateOrProvinceCode: shipmentData.origin.state,
              postalCode: shipmentData.origin.zipCode,
              countryCode: shipmentData.origin.country || 'MX'
            },
            contactInfo: {
              phone: shipmentData.origin.phone,
              companyName: shipmentData.origin.company || 'Silva Transport',
              fullName: shipmentData.origin.name
            }
          },
          receiverDetails: {
            postalAddress: {
              streetLines: [shipmentData.destination.address],
              city: shipmentData.destination.city,
              stateOrProvinceCode: shipmentData.destination.state,
              postalCode: shipmentData.destination.zipCode,
              countryCode: shipmentData.destination.country || 'MX'
            },
            contactInfo: {
              phone: shipmentData.destination.phone,
              companyName: shipmentData.destination.company || '',
              fullName: shipmentData.destination.name
            }
          }
        },
        content: {
          packages: [{
            weight: shipmentData.package.weight,
            dimensions: {
              length: shipmentData.package.length,
              width: shipmentData.package.width,
              height: shipmentData.package.height
            },
            customerReferences: [{
              value: shipmentData.reference || 'SILVA_SHIPMENT'
            }]
          }]
        },
        labelText: shipmentData.labelText || [],
        documents: []
      };

      const response = await fetch(`${this.baseURL}/parcel/de/v2/shipments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`DHL shipment creation error: ${response.status}`);
      }

      const data = await response.json();
      return this.parseDHLShipment(data);
    } catch (error) {
      console.error('Error creating DHL shipment:', error);
      throw error;
    }
  }

  // Parse DHL shipment response
  parseDHLShipment(data) {
    const shipment = data.shipmentTrackingNumber;
    return {
      trackingNumber: shipment,
      labelUrl: data.documentUrl,
      cost: parseFloat(data.totalPrice || 0),
      service: data.productName,
      estimatedDelivery: data.estimatedDeliveryDate,
      labelData: data.documentData
    };
  }

  // Track shipment with DHL
  async trackShipment(trackingNumber) {
    try {
      const response = await fetch(`${this.baseURL}/parcel/de/v2/tracking/${trackingNumber}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`DHL tracking error: ${response.status}`);
      }

      const data = await response.json();
      return this.parseDHLTracking(data);
    } catch (error) {
      console.error('Error tracking DHL shipment:', error);
      throw error;
    }
  }

  // Parse DHL tracking response
  parseDHLTracking(data) {
    const tracking = data.shipments[0];
    return {
      trackingNumber: tracking.id,
      status: tracking.status,
      events: tracking.events.map(event => ({
        status: event.description,
        location: event.location?.address?.city || 'Unknown',
        timestamp: event.timestamp,
        description: event.description
      })),
      estimatedDelivery: tracking.estimatedTimeOfDelivery,
      actualDelivery: tracking.actualTimeOfDelivery
    };
  }

  // Demo rates fallback
  getDemoRates(origin, destination, weight) {
    return [
      {
        carrier: 'DHL',
        service: 'Express Worldwide',
        serviceCode: 'N',
        cost: 180.00,
        currency: 'USD',
        deliveryTime: '1 día',
        guaranteed: true,
        transitDays: 1
      },
      {
        carrier: 'DHL',
        service: 'Express Envelope',
        serviceCode: 'E',
        cost: 150.00,
        currency: 'USD',
        deliveryTime: '1-2 días',
        guaranteed: false,
        transitDays: 2
      },
      {
        carrier: 'DHL',
        service: 'Ground',
        serviceCode: 'G',
        cost: 95.00,
        currency: 'USD',
        deliveryTime: '3-5 días',
        guaranteed: false,
        transitDays: 4
      }
    ];
  }
}

// Create singleton instance
const realDHLService = new RealDHLService();

export default realDHLService; 